import React, { type PropsWithChildren, type ComponentPropsWithoutRef } from 'react';
import castArray from 'lodash/castArray';
import { Box, xcss, type XCSS } from '@atlaskit/primitives';
import { fg } from '@atlassian/jira-feature-gating';
import {
	CellHoverProvider,
	useCellHoverHandlers,
} from '@atlassian/jira-hover-popover/src/controllers/index.tsx';
import { getColumnOffset } from '../../../ui/issue-table/utils.tsx';
import { useCellWrapper } from '../cell-navigation/cell-wrapper/index.tsx';
import {
	useIsGridNavigationEnabled,
	useIsSingleLineRowHeightEnabled,
} from '../../../controllers/features/selectors.tsx';

export type Props = Omit<
	PropsWithChildren<{
		/**
		 * Optional border styles to apply to the `td` element. If not provided, no borders will be rendered.
		 */
		border?: XCSS | Array<XCSS | false | undefined>;
		/**
		 * Optional spacing styles to apply to the `td` element. If not provided, default spacing will apply.
		 */
		spacing?: XCSS | Array<XCSS | false | undefined>;
		/**
		 * Optional row number used for keyboard grid navigation
		 */
		row?: number;
		/**
		 * Optional column number used for keyboard grid navigation
		 */
		column?: number;
	}> &
		ComponentPropsWithoutRef<'td'>,
	'onMouseEnter' | 'onMouseLeave' | 'onBlur'
> & {
	isColumnSticky?: boolean;
	stickyOffsets?: number;
	isColumnResizing?: boolean;
	hasRightShadow?: boolean;
};

export const TableCell = (props: Props) => {
	const isSingleLineRowHeightEnabled = useIsSingleLineRowHeightEnabled();
	return isSingleLineRowHeightEnabled ? (
		<CellHoverProvider>
			<TableCellInner {...props} />
		</CellHoverProvider>
	) : (
		<TableCellInner {...props} />
	);
};

const TableCellInner = ({
	children,
	border,
	spacing,
	row,
	column,
	isColumnSticky,
	stickyOffsets,
	isColumnResizing,
	hasRightShadow,
	...htmlAttributes
}: Props) => {
	const spacingStyles = spacing !== undefined ? castArray(spacing) : [defaultSpacingStyles];

	const borderStyles = border !== undefined ? castArray(border) : [];

	const { cellRef } = fg('jira_list_grid_pattern_keyboard_nav')
		? // eslint-disable-next-line react-hooks/rules-of-hooks
			useCellWrapper({
				row: row == null ? 0 : row + 1,
				col: column == null ? 0 : column,
			})
		: { cellRef: null };

	const isSingleLineRowHeightEnabled = useIsSingleLineRowHeightEnabled();
	let singleLineRowHeightProps = {};

	if (isSingleLineRowHeightEnabled) {
		// eslint-disable-next-line react-hooks/rules-of-hooks
		const { handleMouseEnter, handleMouseLeave } = useCellHoverHandlers();
		singleLineRowHeightProps = {
			onMouseEnter: handleMouseEnter,
			onMouseLeave: handleMouseLeave,
		};
	}
	const isGridNavigationEnabled = useIsGridNavigationEnabled();
	return (
		<Box
			as="td"
			// eslint-disable-next-line react/jsx-props-no-spreading
			{...(fg('issue_table_frozen_columns') && {
				style: {
					left: isColumnSticky
						? getColumnOffset(stickyOffsets ?? 0, !!isColumnResizing)
						: undefined,
					zIndex: isColumnSticky ? 2 : undefined,
				},
			})}
			xcss={[
				baseStyles,
				...spacingStyles,
				...borderStyles,
				isGridNavigationEnabled && keyboardNavigationStyles,
				isColumnSticky && fg('issue_table_frozen_columns') && stickyStyles,
				hasRightShadow && fg('issue_table_frozen_columns') && shadowStyles,
			]}
			// eslint-disable-next-line react/jsx-props-no-spreading
			{...(fg('jira_list_grid_pattern_keyboard_nav') && {
				ref: cellRef,
			})}
			// eslint-disable-next-line react/jsx-props-no-spreading
			{...htmlAttributes}
			// eslint-disable-next-line react/jsx-props-no-spreading
			{...singleLineRowHeightProps}
			// eslint-disable-next-line react/jsx-props-no-spreading
			{...(isGridNavigationEnabled && { tabIndex: -1 })}
		>
			{children}
		</Box>
	);
};

const baseStyles = xcss({
	background: 'inherit',
	// Cross-browser CSS hack to allow inline editable fields to fill the entire space of their `td`. The
	// preferred long-term solution is to adopt a flexbox layout, but until then, please do extensive
	// cross-browser testing before you update this rule.
	height: '100%',
	position: 'relative',
});

const keyboardNavigationStyles = xcss({
	position: 'relative',
	':focus': {
		outline: 'unset',
	},
	':focus::before': {
		display: 'block',
		content: '" "',
		position: 'absolute',
		height: '100%',
		width: '100%',
		inset: '0px',
		borderColor: 'color.border.focused',
		borderWidth: '2px',
		borderStyle: 'solid',
		boxSizing: 'border-box',
	},
});

const defaultSpacingStyles = xcss({
	paddingBlock: 'space.050',
	paddingInline: 'space.100',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-selectors -- Ignored via go/DSP-18766
	':first-of-type': {
		paddingLeft: 'space.100',
	},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-selectors -- Ignored via go/DSP-18766
	':last-of-type': {
		paddingRight: 'space.100',
	},
});

const stickyStyles = xcss({
	position: 'sticky',
});

const shadowStyles = xcss({
	'::after': {
		height: '100%',
		width: '4px',
		content: "''",
		opacity: 0.5,
		transition: 'opacity 0.25s',
		background: 'linear-gradient(90deg, #0000001a 0%, #0000)',
		position: 'absolute',
		right: '-5px',
		top: 'space.0',
	},
});
